import { useAtom } from "jotai";
import { type FC, useCallback } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { EventSelector } from "../../../event/components/event-selector";
import type { EventKey } from "../../../event/server/supabase-event-service";
import type { MomentFormMoment } from "../../../moment/types/moment-form-moment";
import { eventsAtom } from "../../../moment/types/store";
import { isSurveyCompletedKey } from "../../../survey/utils/survey-event-key";
import type { ScheduleFormProps } from "../schedule-form";

export const ScheduleEventSelector: FC<Omit<ScheduleFormProps, "showValidationErrors">> = ({
  reloadMergeTags,
  disabled,
}) => {
  const [eventKeys, setEventKeys] = useAtom(eventsAtom);
  const { control, getValues, setValue } = useFormContext<MomentFormMoment>();

  useWatch({
    control,
    name: ["target_channel"],
  });
  const [targetChannel] = getValues(["target_channel"]);

  const onChangeEvent = useCallback(
    (event: EventKey | null, newEvents: EventKey[]): void => {
      reloadMergeTags();

      setEventKeys(newEvents);
      setValue("schedule.event", event);

      if (event?.key === "birthday") {
        // birthdays are always initial and repeat
        setValue("schedule.event_initial", true);
        setValue("schedule.event_repeat", true);
      } else {
        // calendar events can always do initial and can do repeats if the event has a repeat
        // non-calendar events use allow_initial/allow_repeats rules to decide
        setValue(
          "schedule.event_initial",
          event && (event.calendar || event.allow_initial) ? true : false,
        );
        setValue(
          "schedule.event_repeat",
          event && event.repeat && (event.calendar || event.allow_repeats) ? true : false,
        );
      }
    },
    [reloadMergeTags, setEventKeys, setValue],
  );

  return (
    <Controller
      name="schedule.event"
      control={control}
      render={({ field }) => (
        <EventSelector
          sx={{ paddingLeft: 1, maxWidth: "100%" }}
          value={field.value}
          events={
            eventKeys?.filter(
              ({ calendar, key }) =>
                (targetChannel ? calendar : true) && !isSurveyCompletedKey(key),
            ) ?? []
          }
          onChangeEvent={(event, newEvents) => {
            field.onChange(event);
            onChangeEvent(event, newEvents);
          }}
          disabled={disabled}
        />
      )}
    />
  );
};
